import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import {
  Clock, CheckCircle, XCircle, MapPin, Search, Filter,
  ChevronRight, Star, User, Loader2, Calendar, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configurations
const statusConfig = {
  requested: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50', text: 'Requested' },
  searching: { icon: Search, color: 'text-yellow-500', bg: 'bg-yellow-50', text: 'Searching', animated: true },
  offered: { icon: User, color: 'text-orange-500', bg: 'bg-orange-50', text: 'Offered', animated: true },
  accepted: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', text: 'Accepted' },
  assigned: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', text: 'Assigned' },
  en_route: { icon: MapPin, color: 'text-blue-500', bg: 'bg-blue-50', text: 'En Route' },
  arrived: { icon: MapPin, color: 'text-purple-500', bg: 'bg-purple-50', text: 'Arrived' },
  in_progress: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-50', text: 'In Progress', animated: true },
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', text: 'Completed' },
  cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', text: 'Cancelled' },
  expired: { icon: XCircle, color: 'text-orange-500', bg: 'bg-orange-50', text: 'Expired' },
};

// Job Card Component
const JobCard = ({ job, onClick }) => {
  const config = statusConfig[job.status] || statusConfig.requested;
  const StatusIcon = config.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount, currency) => {
    const symbols = { GBP: '£', USD: '$', EUR: '€' };
    return `${symbols[currency] || currency}${amount.toFixed(2)}`;
  };
  
  const isActive = !['completed', 'cancelled', 'expired'].includes(job.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border border-zinc-200 overflow-hidden hover:border-zinc-300 hover:shadow-sm transition-all cursor-pointer ${
        isActive ? 'border-l-4 border-l-zinc-900' : ''
      }`}
      onClick={onClick}
      data-testid={`job-card-${job.id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-zinc-900">{job.category}</h3>
            <p className="text-sm text-zinc-500">{job.subcategory}</p>
          </div>
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bg}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${config.color} ${config.animated ? 'animate-pulse' : ''}`} />
            <span className={`text-xs font-medium ${config.color}`}>{config.text}</span>
          </div>
        </div>
        
        <p className="text-sm text-zinc-600 line-clamp-2 mb-3">{job.description}</p>
        
        {/* Contractor info (if assigned) */}
        {job.contractor && (
          <div className="flex items-center gap-2 py-2 px-3 bg-zinc-50 rounded-lg mb-3">
            <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-zinc-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-zinc-900">{job.contractor.name}</p>
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Star className="w-3 h-3 text-zinc-900 fill-zinc-900" />
                <span>{job.contractor.rating?.toFixed(1) || '5.0'}</span>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-zinc-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(job.created_at)}
            </span>
            {job.address_text && (
              <span className="flex items-center gap-1 truncate max-w-[150px]">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{job.address_text}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-zinc-900">
              {formatCurrency(job.estimated_price, job.currency)}
            </span>
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const MyJobsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort jobs
  const filteredJobs = jobs
    .filter(job => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return !['completed', 'cancelled', 'expired'].includes(job.status);
      if (statusFilter === 'completed') return job.status === 'completed';
      if (statusFilter === 'cancelled') return ['cancelled', 'expired'].includes(job.status);
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      }
      if (sortBy === 'price_high') {
        return b.estimated_price - a.estimated_price;
      }
      if (sortBy === 'price_low') {
        return a.estimated_price - b.estimated_price;
      }
      return 0;
    });

  // Stats
  const activeCount = jobs.filter(j => !['completed', 'cancelled', 'expired'].includes(j.status)).length;
  const completedCount = jobs.filter(j => j.status === 'completed').length;
  const totalSpent = jobs
    .filter(j => j.status === 'completed')
    .reduce((sum, j) => sum + (j.estimated_price || 0), 0);

  if (loading) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">My Jobs</h1>
            <p className="text-zinc-500 text-sm mt-1">
              {jobs.length} total • {activeCount} active • {completedCount} completed
            </p>
          </div>
          <Button
            onClick={() => navigate('/create-job')}
            className="bg-zinc-900 hover:bg-zinc-800"
            data-testid="create-job-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request a Zolt
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Total Jobs</p>
            <p className="text-2xl font-bold text-zinc-900">{jobs.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Active</p>
            <p className="text-2xl font-bold text-zinc-900">{activeCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Completed</p>
            <p className="text-2xl font-bold text-zinc-900">{completedCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <p className="text-sm text-zinc-500">Total Spent</p>
            <p className="text-2xl font-bold text-zinc-900">£{totalSpent.toFixed(0)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <span className="text-sm text-zinc-600">Filter:</span>
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px]" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="price_high">Price: High-Low</SelectItem>
              <SelectItem value="price_low">Price: Low-High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Jobs List */}
        <div className="space-y-3">
          <AnimatePresence>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No jobs found</h3>
                <p className="text-zinc-500 mb-6">
                  {statusFilter !== 'all' 
                    ? 'Try changing your filters' 
                    : 'Request your first Zolt to get started'}
                </p>
                {statusFilter === 'all' && (
                  <Button
                    onClick={() => navigate('/create-job')}
                    className="bg-zinc-900 hover:bg-zinc-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Request a Zolt
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </DashboardShell>
  );
};

export default MyJobsPage;
